import { Form as FactorialForm } from 'factorial-form'

import type {
  ReportShare,
  ReportShareMode,
  ReportShareVisibility,
} from 'modules/reports/store/models/ReportShare'

interface ShareFormFields {
  id?: number
  granterId: number
  granteeId: number
  mode: ReportShareMode
  visibility: ReportShareVisibility
  status: 'new' | 'persisted' | 'deleted'
}

export class ReportShareForm extends FactorialForm {
  static fromExistingShare(share: ReportShare) {
    return new ReportShareForm({
      id: share.id,
      granterId: share.granterId,
      granteeId: share.granteeId,
      mode: share.mode,
      visibility: share.visibility,
      status: 'persisted',
    })
  }

  constructor(values: ShareFormFields) {
    super(values, {
      id: 'number',
      granterId: 'number',
      granteeId: 'number',
      mode: 'string',
      visibility: 'string',
      status: 'string',
    })
  }

  get id(): number | undefined {
    const value = this.get('id').value

    return value ? Number(value) : undefined
  }

  get granterId(): number {
    return Number(this.get('granterId').value)
  }

  get granteeId(): number {
    return Number(this.get('granteeId').value)
  }

  get mode(): ReportShareMode {
    return this.get('mode').value
  }

  get visibility(): ReportShareVisibility {
    return this.get('visibility').value
  }

  get status(): string {
    return this.get('status').value
  }

  get isNew(): boolean {
    return this.status === 'new'
  }

  get isPersisted(): boolean {
    return this.status === 'persisted'
  }

  get isModified(): boolean {
    return this.isPersisted && (this.get('mode').isDirty || this.get('visibility').isDirty)
  }

  get isDeleted(): boolean {
    return this.status === 'deleted'
  }

  restore(): void {
    if (this.status !== 'delete') {
      throw Error("Invariant: Shouldn't be calling restore() on a non-deleted form.")
    }

    this.get('status').set('persisted')
  }

  changeMode(mode: ReportShareMode): void {
    this.get('mode').set(mode)
  }

  changeVisibility(visibility: ReportShareVisibility): void {
    this.get('visibility').set(visibility)
  }

  delete(): void {
    if (this.status !== 'persisted') {
      throw Error(
        "Invariant: Shouldn't be calling delete() on a non-persisted share, just discard the form."
      )
    }

    this.get('status').set('deleted')
  }
}
